<template>
  <div class="bg-[#f6f1ea]">
    <HomeLuxuryHero
      :current-state="heroState"
      :has-current-opening="heroState === 'open'"
      :image-focal="heroImageFocal"
      :image-src="heroImageSrc"
      :video-src="heroVideoSrc"
    />

    <!-- Section 1 : Précommandes en cours -->
    <LazyHomeLuxuryPreorderDrops
      :drops="activeDrops"
      :collections="homeCollections"
    />

    <!-- Manifesto: short inspiring line (long origin story archived in doc/home-origin-story.md) -->
    <section class="bg-[#0e0d0b] text-[#ece6dc]">
      <div class="mx-auto max-w-[760px] px-6 py-20 text-center sm:py-28">
        <p class="mb-6 text-[0.7rem] uppercase tracking-[0.32em] text-[#ece6dc]/55">{{ manifestoCopy.eyebrow }}</p>
        <p class="font-['Playfair_Display'] text-[1.45rem] leading-[1.6] text-[#f5efe5] sm:text-[1.9rem] sm:leading-[1.55]">{{ manifestoCopy.text }}</p>
        <NuxtLink
          :to="homeIsFrench ? '/fr/brand' : '/brand'"
          no-prefetch
          class="mt-10 inline-flex whitespace-nowrap border-b border-[#ece6dc]/25 pb-0.5 text-[0.72rem] uppercase tracking-[0.32em] text-[#ece6dc]/70 transition-colors hover:border-[#ece6dc]/60 hover:text-[#ece6dc]"
        >
          {{ manifestoCopy.cta }}
        </NuxtLink>
      </div>
    </section>

    <!-- Section 2 : Collections permanentes -->
    <LazyHomeLuxuryPermanentCollections
      :collections="homeCollections"
      :preorder-product-types="preorderProductTypes"
    />

    <!-- Editorial house section -->
    <section class="bg-white">
      <div class="mx-auto max-w-[640px] px-6 py-16 sm:py-20">
        <p class="text-[0.7rem] uppercase tracking-[0.32em] text-black/45 mb-5">{{ storyCopy.house.eyebrow }}</p>
        <div class="space-y-3.5">
          <p class="text-[0.97rem] leading-[1.7] text-black/80 sm:text-[1.02rem]" v-html="storyCopy.house.intro" />
          <div v-if="storyHouseExpanded" class="space-y-3.5 text-[0.97rem] leading-[1.7] text-black/80 sm:text-[1.02rem]" v-html="storyCopy.house.rest" />
          <button
            v-if="!storyHouseExpanded"
            class="mt-4 inline-flex max-w-full whitespace-nowrap text-[0.72rem] uppercase tracking-[0.32em] text-black/45 hover:text-black/70 transition-colors border-b border-black/20 pb-0.5"
            @click="storyHouseExpanded = toggleHomeStorySection('house', storyHouseExpanded)"
          >
            {{ editorialUiCopy.readMore }}
          </button>
          <p class="text-[1rem] text-black/60">
            {{ instagramSeeMore }}
            <a href="https://kendrick.paris/s/sig" target="_blank" rel="noopener" class="inline text-black border-b border-black/15 pb-0.5 hover:border-black/40 transition-colors">@kendrick.paris</a>
          </p>
          <button
            v-if="storyHouseExpanded"
            class="inline-flex max-w-full whitespace-nowrap text-[0.72rem] uppercase tracking-[0.32em] text-black/45 hover:text-black/70 transition-colors border-b border-black/20 pb-0.5"
            @click="storyHouseExpanded = toggleHomeStorySection('house', storyHouseExpanded)"
          >
            {{ editorialUiCopy.readLess }}
          </button>
        </div>
      </div>
    </section>

    <!-- Editorial proof section -->
    <section class="border-y border-[#1a1714]/10 bg-[#f6f1ea] text-[#1a1714]">
      <div class="mx-auto max-w-[1680px] px-6 py-20 sm:px-8 lg:px-14 lg:py-28">
        <div class="grid gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:gap-20">
          <div>
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">
              {{ recordCopy.eyebrow }}
            </p>
            <h2 class="mt-5 max-w-[13ch] font-['Playfair_Display'] text-[2.65rem] leading-[1.02] tracking-[0] sm:text-[3.45rem]">
              {{ recordCopy.title }}
            </h2>
          </div>

          <div class="space-y-9 text-[0.95rem] leading-[1.8] text-[#1a1714]/75 lg:pt-4">
            <div class="grid gap-7 md:grid-cols-2">
              <div v-for="group in recordCopy.groups" :key="group.eyebrow">
                <p class="text-[0.62rem] uppercase tracking-[0.28em] text-[#1a1714]/40">
                  {{ group.eyebrow }}
                </p>
                <p class="mt-3">
                  {{ group.text }}
                </p>
              </div>
            </div>

            <div class="grid items-start gap-7 md:grid-cols-2">
              <blockquote
                v-for="quote in recordCopy.quotes"
                :key="quote.text"
                class="border-l border-[#1a1714]/25 pl-6 font-['Playfair_Display'] text-[1.2rem] italic leading-[1.6] text-[#1a1714]/80 sm:text-[1.35rem]"
              >
                <span>{{ quote.text }}</span>
                <footer
                  v-if="quote.footer"
                  class="mt-4 font-sans text-[0.68rem] not-italic uppercase tracking-[0.18em] text-[#1a1714]/60"
                >
                  <a
                    v-if="quote.footerHref"
                    :href="quote.footerHref"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="border-b border-current/20 transition hover:border-current/60"
                  >
                    {{ quote.footer }}
                  </a>
                  <template v-else>
                    {{ quote.footer }}
                  </template>
                </footer>
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </section>

    <LazyHomeLuxuryAccessProcess :campaign="primaryCampaign" />

    <!-- Editorial craft section -->
    <section class="bg-[#0e0d0b] text-[#ece6dc]">
      <div class="mx-auto max-w-[640px] px-6 py-20 sm:py-28">
        <p class="text-[0.7rem] uppercase tracking-[0.32em] text-[#ece6dc]/55 mb-5">{{ storyCopy.craft.eyebrow }}</p>
        <p class="font-['Playfair_Display'] text-[1.24rem] leading-[1.78] text-[#f5efe5] sm:text-[1.36rem] sm:leading-[1.72]">{{ storyCopy.craft.body }}</p>
        <p v-if="!homeIsFrench" class="mt-7 text-[0.72rem] uppercase tracking-[0.3em] text-[#ece6dc]/48">
          <NuxtLink to="/french-heels" class="border-b border-[#ece6dc]/20 pb-1 transition hover:border-[#ece6dc]/55 hover:text-[#ece6dc]">
            French heels made in Italy
          </NuxtLink>
        </p>
      </div>
    </section>

    <LazyHomeLuxuryPrivateAccess />

    <!-- Register panels in global dev bar -->
    <component
      :is="homeAssetsKitDumpComponent"
      v-if="homeAssetsKitDumpComponent"
      title="Home — Preorder Drops"
      :loading="dropsPending"
      :error="dropsError"
      :api-routes="['/api/home-preorder-drops']"
      :media-items="devDropsMediaItems"
    >
      <div v-if="allActiveDrops.length" class="mt-2">
        <p class="mb-1 text-gray-400">
          ── Drops API: {{ allActiveDrops.length }} · Home focus: {{ activeDrops.length }} ──
        </p>
        <pre>{{ JSON.stringify(devDropsPayload, null, 2) }}</pre>
      </div>
      <template v-else-if="!dropsPending">
        <p class="mb-2 text-yellow-400">No active preorder drops.</p>
        <pre>{{ JSON.stringify(devDropsPayload, null, 2) }}</pre>
      </template>
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { getEditorialRecordCopy, getEditorialStoryCopy, getHomeManifestoCopy, getEditorialUiCopy } from '~/utils/editorialStoryCopy'
import { useSiteLocale } from '~/composables/useSiteLocale'

const route = useRoute()
const homeLocale = useSiteLocale()

const INSTAGRAM_SEE_MORE: Record<string, string> = {
  en: 'See more on Instagram',
  fr: 'Voir plus sur Instagram',
  es: 'Ver más en Instagram',
  pt: 'Ver mais no Instagram',
  ja: 'Instagramでもっと見る',
  ko: '인스타그램에서 더 보기',
  de: 'Mehr auf Instagram',
  it: 'Scopri di più su Instagram',
  zh: '在 Instagram 查看更多'
}
const instagramSeeMore = computed(() => INSTAGRAM_SEE_MORE[homeLocale.value] || INSTAGRAM_SEE_MORE.en)
const homeIsFrench = computed(() => homeLocale.value === 'fr')
const homeSignalsKit = import.meta.client ? useSignalsKit() : null
const storyCopy = computed(() => getEditorialStoryCopy(homeLocale.value))
const manifestoCopy = computed(() => getHomeManifestoCopy(homeLocale.value))
const recordCopy = computed(() => getEditorialRecordCopy(homeLocale.value))
const editorialUiCopy = computed(() => getEditorialUiCopy(homeLocale.value))

const storyHouseExpanded = ref(false)
const privateAccessHash = '#private-access'
let privateAccessScrollFrame = 0
let privateAccessScrollTimers: ReturnType<typeof setTimeout>[] = []
let privateAccessFocusTimers: ReturnType<typeof setTimeout>[] = []

const clearPrivateAccessScroll = () => {
  if (!import.meta.client) return

  if (privateAccessScrollFrame) {
    window.cancelAnimationFrame(privateAccessScrollFrame)
    privateAccessScrollFrame = 0
  }

  for (const timer of privateAccessScrollTimers) {
    clearTimeout(timer)
  }

  privateAccessScrollTimers = []

  for (const timer of privateAccessFocusTimers) {
    clearTimeout(timer)
  }

  privateAccessFocusTimers = []
}

const getVisibleHeaderOffset = () => {
  const header = document.querySelector<HTMLElement>('.hd')
  if (!header || header.classList.contains('hd--hidden')) return 16
  return Math.round(header.getBoundingClientRect().height + 16)
}

const scrollToPrivateAccess = async (behavior: ScrollBehavior, attempt = 0) => {
  if (!import.meta.client || route.hash !== privateAccessHash) return

  await nextTick()

  const target = document.getElementById('private-access')
  if (!target) {
    // The section is a lazy/async component: on a cold dev compile (or a slow
    // connection) it can take several seconds to mount, well past a short
    // retry budget — keep polling for longer rather than giving up silently.
    if (attempt < 60) {
      queuePrivateAccessScroll(behavior, 100, attempt + 1)
    }
    return
  }

  if (privateAccessScrollFrame) {
    window.cancelAnimationFrame(privateAccessScrollFrame)
  }

  privateAccessScrollFrame = window.requestAnimationFrame(() => {
    const top = target.getBoundingClientRect().top + window.scrollY - getVisibleHeaderOffset()
    window.scrollTo({ top: Math.max(0, top), behavior })
    queuePrivateAccessFocus(behavior === 'auto' ? 160 : 620)
    privateAccessScrollFrame = 0
  })
}

function queuePrivateAccessScroll(behavior: ScrollBehavior, delay = 0, attempt = 0) {
  if (!import.meta.client) return

  const timer = setTimeout(() => {
    privateAccessScrollTimers = privateAccessScrollTimers.filter(item => item !== timer)
    void scrollToPrivateAccess(behavior, attempt)
  }, delay)

  privateAccessScrollTimers.push(timer)
}

function queuePrivateAccessFocus(delay = 0, attempt = 0) {
  if (!import.meta.client) return

  const timer = setTimeout(() => {
    privateAccessFocusTimers = privateAccessFocusTimers.filter(item => item !== timer)
    const input = document.getElementById('private-access-first-name') as HTMLInputElement | null

    if (!input) {
      if (attempt < 60) queuePrivateAccessFocus(100, attempt + 1)
      return
    }

    input.focus({ preventScroll: true })
  }, delay)

  privateAccessFocusTimers.push(timer)
}

const queueSettledPrivateAccessScroll = (behavior: ScrollBehavior) => {
  clearPrivateAccessScroll()
  queuePrivateAccessScroll(behavior)
  queuePrivateAccessScroll(behavior, 450)
  queuePrivateAccessFocus(behavior === 'auto' ? 700 : 1050)
}

const toggleHomeStorySection = (section: 'origin' | 'house', expanded: boolean) => {
  const nextExpanded = !expanded

  homeSignalsKit?.track('editorial_story_toggle', {
    section,
    page_context: 'home',
    page_path: route.path,
    locale: homeLocale.value,
    interaction_type: nextExpanded ? 'read_more' : 'read_less'
  })

  return nextExpanded
}

const homeAssetsKitDumpComponent = shallowRef<any>(null)
const focusedProductType = 'ankle-boots'
const focusedCollectionSlug = 'align'
const focusedColor = 'black'
const heroVideoSrc = '/video/ankleboots.mp4'
const homeLookbookFocal = { x: 50, y: 18 }

type FocalPoint = {
  x?: number
  y?: number
}

type PreorderDrop = {
  id: string
  title?: string
  description?: string
  start_date?: string | null
  end_date?: string | null
  preorder_price?: number | null
  current_quantity?: number | null
  target_quantity?: number | null
  product_type?: string
  color?: string
  status?: string
}

type HomeCollection = {
  slug?: string
  name?: string
  availability?: string | null
  totalInventory?: number
  variantsByColor?: Record<string, {
    colorKey?: string
    focal?: FocalPoint | null
    image?: string | null
    images?: string[] | null
    photos?: string[] | null
    photos_zoom?: string[] | null
    mediaFocus?: Record<string, FocalPoint> | null
    price?: number
    inventoryQuantity?: number | null
    availability?: string | null
  }>
}

// Fetch all active preorder drops
const {
  data: dropsResponse,
  pending: dropsPending,
  error: dropsAsyncError
} = useAsyncData(
  'home-luxury-preorder-drops',
  () => $fetch<{ drops?: PreorderDrop[]; error?: string | null }>('/api/home-preorder-drops'),
  {
    lazy: import.meta.client,
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] ?? nuxtApp.static.data[key],
    default: () => ({ drops: [], error: null })
  }
)

const preorderPriority: Record<string, number> = {
  'ankle-boots': 0,
  boots: 1,
  pumps: 2,
  sandals: 3
}

const allActiveDrops = computed(() => {
  const drops = [...(dropsResponse.value?.drops || [])]

  return drops.sort((left, right) => {
    const leftPriority = preorderPriority[left.product_type || ''] ?? 99
    const rightPriority = preorderPriority[right.product_type || ''] ?? 99

    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority
    }

    const leftEnd = left.end_date ? new Date(left.end_date).getTime() : Number.MAX_SAFE_INTEGER
    const rightEnd = right.end_date ? new Date(right.end_date).getTime() : Number.MAX_SAFE_INTEGER

    return leftEnd - rightEnd
  })
})
const activeDrops = computed(() =>
  allActiveDrops.value.filter((drop) => {
    const productType = drop.product_type || focusedProductType
    const color = String(drop.color || focusedColor).toLowerCase()
    return productType === focusedProductType && color === focusedColor
  })
)
const dropsError = computed(() => (
  dropsAsyncError.value?.message ||
  dropsResponse.value?.error ||
  null
))

// List of product types that have active preorders (for badge display)
const preorderProductTypes = computed(() =>
  [...new Set(activeDrops.value.map(d => d.product_type).filter(Boolean))] as string[]
)

const {
  data: homeCollectionsResponse,
  error: homeCollectionsFetchError
} = useAsyncData(
  `home-luxury-assetskit-home-${homeLocale.value}`,
  () => $fetch<{ collections?: HomeCollection[]; error?: string | null }>('/api/assetskit-home', {
    query: { lang: homeLocale.value }
  }),
  {
    lazy: import.meta.client,
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] ?? nuxtApp.static.data[key],
    default: () => ({
      collections: [],
      error: null
    }),
    watch: [homeLocale]
  }
)

const sourceHomeCollections = computed(() => homeCollectionsResponse.value?.collections || [])

const focusedDrop = computed(() => activeDrops.value[0] || null)
const focusedPreorderPrice = computed(() => {
  const price = Number(focusedDrop.value?.preorder_price ?? 680)
  return Number.isFinite(price) && price > 0 ? price : 680
})
const focusedTargetQuantity = computed(() => {
  const quantity = Number(focusedDrop.value?.target_quantity ?? NaN)
  return Number.isFinite(quantity) && quantity > 0 ? quantity : null
})
const focusedAvailability = computed(() => heroState.value === 'open' ? 'preorder' : 'in_stock')

const homeCollections = computed<HomeCollection[]>(() => {
  return sourceHomeCollections.value.map((collection) => {
    if (collection.slug !== focusedCollectionSlug) return collection

    const variantsByColor = { ...(collection.variantsByColor || {}) }
    const focusedVariant = variantsByColor[focusedColor]

    if (focusedVariant) {
      variantsByColor[focusedColor] = {
        ...focusedVariant,
        availability: focusedAvailability.value,
        inventoryQuantity: focusedTargetQuantity.value ?? focusedVariant.inventoryQuantity,
        price: focusedPreorderPrice.value
      }
    }

    return {
      ...collection,
      availability: focusedAvailability.value,
      totalInventory: focusedTargetQuantity.value ?? collection.totalInventory,
      variantsByColor
    }
  })
})

// Primary campaign for the access process section (first drop, if any)
const primaryCampaign = computed(() => {
  const first = activeDrops.value[0]
  if (!first) return null
  return {
    current_quantity: first.current_quantity,
    end_date: first.end_date,
    start_date: first.start_date,
    target_quantity: first.target_quantity
  }
})

// Hero state: open if any drop is active
const heroState = computed<'open' | 'closed'>(() => {
  if (activeDrops.value.length === 0) return 'closed'

  // Check if any drop still has allocation space
  const hasOpenDrop = activeDrops.value.some((drop) => {
    const current = Number(drop.current_quantity ?? 0)
    const target = Number(drop.target_quantity ?? 0)
    if (target > 0 && current >= target) return false
    return true
  })

  return hasOpenDrop ? 'open' : 'closed'
})
const focusedCollection = computed(() =>
  homeCollections.value.find(collection => collection.slug === focusedCollectionSlug) || null
)

const focusedCollectionVariant = computed(() =>
  focusedCollection.value?.variantsByColor?.[focusedColor] ||
  focusedCollection.value?.variantsByColor?.black ||
  null
)

const heroImageSrc = computed(() =>
  focusedCollectionVariant.value?.photos?.[0] ||
  focusedCollectionVariant.value?.images?.[0] ||
  focusedCollectionVariant.value?.photos_zoom?.[0] ||
  focusedCollectionVariant.value?.image ||
  ''
)

const heroImageFocal = computed(() =>
  (heroImageSrc.value && focusedCollectionVariant.value?.mediaFocus?.[heroImageSrc.value]) ||
  focusedCollectionVariant.value?.focal ||
  homeLookbookFocal
)

// ── Dev bar media items (passed to AssetsKitDump) ──
const devDropsMediaItems = computed(() =>
  activeDrops.value.map(() => ({
    label: `${focusedCollectionSlug} · ${focusedColor}`,
    url: heroImageSrc.value
  }))
)

const devDropsPayload = computed(() => ({
  homeFocusProductType: focusedProductType,
  homeFocusColor: focusedColor,
  homeFocusCollectionSlug: focusedCollectionSlug,
  homeLookbookImage: heroImageSrc.value,
  rawDrops: allActiveDrops.value,
  focusedDrops: activeDrops.value,
  focusedCollection: homeCollections.value[0],
  homeCollectionsError: homeCollectionsFetchError.value?.message || homeCollectionsResponse.value?.error || null,
  error: dropsResponse.value?.error || null
}))

onMounted(() => {
  if (route.hash === privateAccessHash) {
    queueSettledPrivateAccessScroll('auto')
  }

  if (!import.meta.dev) return

  void import('~/components/AssetsKitDump.vue')
    .then(({ default: AssetsKitDump }) => {
      homeAssetsKitDumpComponent.value = AssetsKitDump
    })
    .catch(() => {})
})

watch(
  () => route.hash,
  (hash) => {
    if (hash === privateAccessHash) {
      queueSettledPrivateAccessScroll('smooth')
    }
  }
)

onBeforeUnmount(() => {
  clearPrivateAccessScroll()
})
</script>
